<template>
    <v-dialog v-model="openMode" max-width="860px" content-class="cd-dialog" overlay-color="#0d2c6d"
        overlay-opacity="0.55" transition="dialog-bottom-transition" :persistent="progressShow || levelBusy">
        <v-card class="cd-card">

            <!-- ───────── HEADER ───────── -->
            <div class="cd-head">
                <div class="cd-head__main">
                    <div class="cd-head__icon" aria-hidden="true">
                        <v-icon color="#fff" size="22">{{ isEdit ? 'mdi-account-group-outline' : 'mdi-plus-circle-outline' }}</v-icon>
                    </div>
                    <div class="cd-head__text">
                        <span class="cd-eyebrow">{{ isEdit ? 'עריכת קהילה' : 'קהילות' }}</span>
                        <h2 class="cd-title">{{ isEdit ? (originalDetails.name || 'עריכת קהילה') : 'קהילה חדשה' }}</h2>
                    </div>
                </div>
                <button type="button" class="cd-close" @click="openMode = false" :disabled="progressShow || levelBusy"
                    aria-label="סגור">
                    <v-icon color="#fff" size="18">mdi-close</v-icon>
                </button>
            </div>

            <!-- ───────── BODY ───────── -->
            <v-card-text class="cd-body">

                <!-- Section: details -->
                <div class="cd-section">
                    <h3 class="cd-section__title">
                        <span class="cd-section__bar"></span>
                        פרטי הקהילה
                    </h3>
                    <v-form ref="form" @submit.prevent="saveDetails">
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field outlined dense v-model="itemEdited.name" label="שם הקהילה"
                                    prepend-inner-icon="mdi-account-group-outline" :rules="[requiredRule]"
                                    hide-details="auto" autocomplete="off"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field outlined dense v-model="itemEdited.token" label="טוקן במערכת לחומרא"
                                    prepend-inner-icon="mdi-key-variant" :rules="[requiredRule]" hide-details="auto"
                                    autocomplete="off" class="cd-ltr-field"></v-text-field>
                            </v-col>
                        </v-row>
                        <!-- כפתור submit נסתר כדי ש-Enter בשדות ישמור -->
                        <button type="submit" class="cd-hidden-submit" tabindex="-1" aria-hidden="true"></button>
                    </v-form>

                    <div v-if="isEdit" class="cd-sync">
                        <span class="cd-sync__item">
                            <v-icon size="15">mdi-sync</v-icon>
                            שם במערכת לחומרא:
                            <strong :class="{ 'cd-muted': !itemEdited.external_name }">{{ itemEdited.external_name || 'טרם סונכרן' }}</strong>
                        </span>
                        <span class="cd-sync__item">
                            <v-icon size="15">mdi-identifier</v-icon>
                            מזהה:
                            <strong :class="{ 'cd-muted': !itemEdited.external_id }">{{ itemEdited.external_id || '—' }}</strong>
                        </span>
                    </div>
                </div>

                <!-- Section: levels (edit mode only) -->
                <div v-if="isEdit" class="cd-section">
                    <h3 class="cd-section__title">
                        <span class="cd-section__bar"></span>
                        רמות סינון
                        <span class="cd-count">{{ itemEdited.levels.length }}</span>
                        <span class="cd-section__note">
                            <v-icon size="14">mdi-flash-outline</v-icon>
                            הוספה ומחיקה נשמרות מיד
                        </span>
                    </h3>

                    <v-form ref="form2" @submit.prevent="saveNewLevel" class="cd-add-level">
                        <v-text-field ref="levelName" outlined dense v-model="newLevel.level_name" label="שם רמת סינון"
                            :rules="[requiredRule]" hide-details="auto" autocomplete="off"
                            class="cd-add-level__name"></v-text-field>
                        <v-text-field outlined dense v-model="newLevel.level_code" label="קוד" type="number"
                            hide-spin-buttons :rules="[requiredRule, uniqueCodeRule]" hide-details="auto"
                            autocomplete="off" class="cd-add-level__code"></v-text-field>
                        <button type="submit" class="cd-btn cd-btn--add" :disabled="levelBusy">
                            <v-icon size="16" color="#fff">mdi-plus</v-icon>
                            <span>הוסף</span>
                        </button>
                    </v-form>

                    <div class="cd-levels">
                        <div class="cd-levels__head" v-if="itemEdited.levels.length">
                            <span>קוד</span>
                            <span>שם</span>
                            <span>שם במערכת לחומרא</span>
                            <span></span>
                        </div>
                        <transition-group name="cd-level" tag="div" class="cd-levels__body">
                            <div v-for="level in sortedLevels" :key="level._id" class="cd-level"
                                :class="{ 'cd-level--new': level._id === recentlyAddedId }">
                                <span class="cd-level__code">{{ level.level_code }}</span>
                                <span class="cd-level__name">{{ level.level_name }}</span>
                                <span class="cd-level__ext" :class="{ 'cd-muted': !level.external_name }">
                                    {{ level.external_name || 'טרם סונכרן' }}
                                </span>
                                <span class="cd-level__actions">
                                    <button type="button" class="cd-level__delete"
                                        :class="{ 'cd-level__delete--confirm': pendingDeleteId === level._id }"
                                        :disabled="levelBusy" @click="onDeleteLevelClick(level)"
                                        :aria-label="pendingDeleteId === level._id ? 'אישור מחיקה' : 'מחיקת רמת סינון'">
                                        <v-icon size="16">{{ pendingDeleteId === level._id ? 'mdi-alert-outline' : 'mdi-delete-outline' }}</v-icon>
                                        <span v-if="pendingDeleteId === level._id">למחוק?</span>
                                    </button>
                                </span>
                            </div>
                        </transition-group>
                        <div v-if="!itemEdited.levels.length" class="cd-levels__empty">
                            <v-icon size="26">mdi-filter-variant-plus</v-icon>
                            <span>עדיין אין רמות סינון לקהילה הזו. אפשר להוסיף כאן למעלה.</span>
                        </div>
                    </div>
                </div>
            </v-card-text>

            <!-- ───────── FOOTER ───────── -->
            <div class="cd-foot">
                <v-progress-linear v-if="progressShow || levelBusy" indeterminate color="#fdb721" height="2" />
                <div class="cd-foot__actions">
                    <span v-if="isEdit && detailsDirty" class="cd-foot__dirty">
                        <v-icon size="14">mdi-circle-medium</v-icon>
                        יש שינויים בפרטי הקהילה שלא נשמרו
                    </span>
                    <button type="button" class="cd-btn cd-btn--ghost" @click="openMode = false"
                        :disabled="progressShow || levelBusy">{{ isEdit ? 'סגור' : 'ביטול' }}</button>
                    <button type="button" class="cd-btn cd-btn--primary" @click="saveDetails"
                        :disabled="progressShow || levelBusy || (isEdit && !detailsDirty)">
                        <v-icon v-if="!progressShow" size="16" color="#fff">{{ isEdit ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                        <span>{{ progressShow ? 'שומר...' : (isEdit ? 'שמור פרטים' : 'צור קהילה') }}</span>
                    </button>
                </div>
            </div>
        </v-card>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

    </v-dialog>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'

export default {
    props: {
        itemToEdit: Object,
        formTitle: String,
        value: { type: Boolean, default: false },
    },
    components: {
        SnackBar,
    },
    data: () => ({
        progressShow: false,
        levelBusy: false,
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        mode: "create",
        itemEdited: {
            _id: "",
            name: "",
            token: "",
            external_name: "",
            external_id: "",
            levels: [],
        },
        originalDetails: {
            name: "",
            token: "",
        },
        newLevel: {
            _id: "",
            level_name: "",
            level_code: "",
        },
        pendingDeleteId: null,
        pendingDeleteTimer: null,
        recentlyAddedId: null,
        recentlyAddedTimer: null,
    }),

    methods: {
        saveDetails() {
            if (this.isEdit && !this.detailsDirty) {
                return;
            }
            return this.isEdit ? this.saveEditedItem() : this.saveNewItem();
        },
        async saveNewItem() {

            if (this.$refs.form.validate()) {

                const ivrJSON = JSON.stringify({ community: this.itemEdited });
                this.progressShow = true;
                let token = localStorage.getItem("token");

                try {
                    let api = process.env.VUE_APP_BASE_URL + "/communities/create_community";
                    const res = await fetch(api, ApiServices.requestOptions("POST", ivrJSON, token));
                    this.progressShow = false;
                    const jsonObject = await res.json();
                    if (res.status >= 400) {
                        this.showSnackBar("שגיאה: " + this.errorText(jsonObject), "red");
                    } else if (res.status === 200) {
                        this.itemEdited._id = jsonObject._id;
                        this.itemEdited.name = jsonObject.name || this.itemEdited.name;
                        this.itemEdited.token = jsonObject.token || this.itemEdited.token;
                        this.$emit("communityCreated", this.itemEdited, "הקהילה נוצרה בהצלחה", "green");

                        // נשארים בדיאלוג במצב עריכה כדי להוסיף מיד רמות סינון
                        this.mode = "edit";
                        this.rememberDetails();
                        this.$nextTick(() => this.focusLevelName());
                    }
                } catch (error) {
                    this.progressShow = false;
                    this.showSnackBar("שגיאה ביצירת הקהילה: " + error, "red");
                }
            }

        },
        async saveEditedItem() {

            if (this.$refs.form.validate()) {

                const ivrJSON = JSON.stringify({ community: this.itemEdited });
                this.progressShow = true;
                let token = localStorage.getItem("token");

                try {
                    let api = process.env.VUE_APP_BASE_URL + "/communities/edit_community";
                    const res = await fetch(api, ApiServices.requestOptions("PUT", ivrJSON, token));
                    this.progressShow = false;
                    const jsonObject = await res.json();
                    if (res.status >= 400) {
                        this.showSnackBar("שגיאה: " + this.errorText(jsonObject), "red");
                    } else if (res.status === 200) {
                        this.$emit("communityEdited", this.itemEdited, "הקהילה עודכנה בהצלחה", "green");
                        this.openMode = false;
                    }
                } catch (error) {
                    this.progressShow = false;
                    this.showSnackBar("שגיאה בעדכון הקהילה: " + error, "red");

                }
            }
        },
        async saveNewLevel() {

            if (this.levelBusy || !this.$refs.form2.validate()) {
                return;
            }

            const levelToAdd = {
                _id: "",
                level_name: String(this.newLevel.level_name).trim(),
                level_code: String(this.newLevel.level_code).trim(),
            };
            const ivrJSON = JSON.stringify({ newLevel: levelToAdd, community: this.itemEdited });
            this.levelBusy = true;
            let token = localStorage.getItem("token");

            try {
                let api = process.env.VUE_APP_BASE_URL + "/communities/create_level";
                const res = await fetch(api, ApiServices.requestOptions("POST", ivrJSON, token));
                this.levelBusy = false;
                const jsonObject = await res.json();
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה: " + this.errorText(jsonObject), "red");
                } else if (res.status === 200) {
                    levelToAdd._id = jsonObject;
                    this.itemEdited.levels.push(levelToAdd);
                    this.$emit("communityEdited", this.itemEdited, "רמת הסינון נוספה בהצלחה", "green");
                    this.highlightLevel(levelToAdd._id);

                    this.newLevel = { _id: "", level_name: "", level_code: "" };
                    this.$refs.form2.resetValidation();
                    this.focusLevelName();
                }
            } catch (error) {
                this.levelBusy = false;
                this.showSnackBar("שגיאה בהוספת רמת הסינון: " + error, "red");
            }
        },
        // מחיקה בשתי לחיצות: הראשונה מבקשת אישור, השנייה (תוך 4 שניות) מוחקת
        onDeleteLevelClick(level) {
            clearTimeout(this.pendingDeleteTimer);
            if (this.pendingDeleteId === level._id) {
                this.pendingDeleteId = null;
                this.deleteLevel(level);
                return;
            }
            this.pendingDeleteId = level._id;
            this.pendingDeleteTimer = setTimeout(() => { this.pendingDeleteId = null; }, 4000);
        },
        async deleteLevel(level) {
            const ivrJSON = JSON.stringify({ level: level, community: this.itemEdited });
            this.levelBusy = true;
            let token = localStorage.getItem("token");

            try {
                let api = process.env.VUE_APP_BASE_URL + "/communities/delete_level";
                const res = await fetch(api, ApiServices.requestOptions("DELETE", ivrJSON, token));
                this.levelBusy = false;
                const jsonObject = await res.json();
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה: " + this.errorText(jsonObject), "red");
                } else if (res.status === 200) {
                    // מוחקים לפי _id ולא לפי מיקום, כי הרשימה מוצגת ממוינת
                    const index = this.itemEdited.levels.findIndex(l => l._id === level._id);
                    if (index !== -1) {
                        this.itemEdited.levels.splice(index, 1);
                    }
                    this.$emit("communityEdited", this.itemEdited, "רמת הסינון נמחקה בהצלחה", "green");
                }
            } catch (error) {
                this.levelBusy = false;
                this.showSnackBar("שגיאה במחיקת רמת הסינון: " + error, "red");
            }
        },
        requiredRule(value) {
            return (value !== null && value !== undefined && String(value).trim() !== "") || "שדה חובה";
        },
        uniqueCodeRule(value) {
            const code = String(value ?? "").trim();
            if (!code) return true;
            const exists = this.itemEdited.levels.some(level => String(level.level_code).trim() === code);
            return !exists || "הקוד כבר קיים בקהילה";
        },
        rememberDetails() {
            this.originalDetails = {
                name: this.itemEdited.name || "",
                token: this.itemEdited.token || "",
            };
        },
        highlightLevel(id) {
            clearTimeout(this.recentlyAddedTimer);
            this.recentlyAddedId = id;
            this.recentlyAddedTimer = setTimeout(() => { this.recentlyAddedId = null; }, 2200);
        },
        focusLevelName() {
            const field = this.$refs.levelName;
            if (field && typeof field.focus === "function") {
                field.focus();
            }
        },
        errorText(jsonObject) {
            if (!jsonObject) return "";
            if (typeof jsonObject === "string") return jsonObject;
            if (typeof jsonObject.message === "string") return jsonObject.message;
            return JSON.stringify(jsonObject.message || jsonObject);
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true
        },
    },
    computed: {
        openMode: {
            get() {
                return this.value
            },
            set(v) {
                return this.$emit('input', v)
            }
        },
        isEdit() {
            return this.mode === "edit";
        },
        detailsDirty() {
            return (this.itemEdited.name || "").trim() !== (this.originalDetails.name || "").trim()
                || (this.itemEdited.token || "").trim() !== (this.originalDetails.token || "").trim();
        },
        sortedLevels() {
            return [...this.itemEdited.levels].sort((a, b) => Number(a.level_code) - Number(b.level_code));
        },
    },
    mounted() {
        if (this.formTitle === "ערוך קהילה") {
            this.mode = "edit";
            this.itemEdited = JSON.parse(JSON.stringify(this.itemToEdit));
            if (!Array.isArray(this.itemEdited.levels)) {
                this.$set(this.itemEdited, "levels", []);
            }
            this.rememberDetails();
        }
    },
    beforeDestroy() {
        clearTimeout(this.pendingDeleteTimer);
        clearTimeout(this.recentlyAddedTimer);
    },
}
</script>

<style scoped>
.cd-card {
    --c-navy: #0d2c6d;
    --c-gold: #fdb721;
    --c-purple: #8066f0;
    --c-purple-soft: rgba(128, 102, 240, 0.10);
    --c-border: #e3e8ef;
    --c-border-soft: #eef2f7;
    --c-text: #0f172a;
    --c-text-soft: #475569;
    --c-text-mute: #94a3b8;
    --c-red: #c74747;
    --c-red-soft: rgba(199, 71, 71, 0.10);
    --c-amber: #b45309;
    --c-ease: cubic-bezier(0.22, 1, 0.36, 1);
    font-family: 'Assistant', 'Heebo', system-ui, sans-serif;
    direction: rtl;
    background: #ffffff !important;
    border-radius: 20px !important;
    overflow: hidden;
}

/* ─── HEADER ─── */
.cd-head {
    background:
        radial-gradient(700px 200px at 90% -40%, rgba(253, 183, 33, 0.26), transparent 60%),
        linear-gradient(135deg, #0d2c6d 0%, #13306f 55%, #1b3a80 100%);
    color: #fff;
    padding: 18px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.cd-head__main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.cd-head__text { min-width: 0; }

.cd-head__icon {
    width: 42px;
    height: 42px;
    flex-shrink: 0;
    border-radius: 11px;
    background: rgba(253, 183, 33, 0.28);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: grid;
    place-items: center;
}

.cd-eyebrow {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 1px;
}

.cd-title {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 800;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cd-close {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: background 0.2s var(--c-ease);
}
.cd-close:hover { background: rgba(255, 255, 255, 0.18); }
.cd-close:disabled { opacity: 0.5; cursor: default; }

/* ─── BODY ─── */
.cd-body {
    padding: 20px 22px 8px !important;
    background: #f7f8fb;
    max-height: 68vh;
    overflow-y: auto;
}

.cd-section {
    margin-bottom: 16px;
    padding: 16px 18px 14px;
    background: #ffffff;
    border: 1px solid var(--c-border-soft);
    border-radius: 14px;
}

.cd-section__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.92rem;
    font-weight: 700;
    color: var(--c-navy);
    margin: 0 0 14px;
}

.cd-section__bar {
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, var(--c-navy), var(--c-gold));
    border-radius: 2px;
}

.cd-section__note {
    margin-inline-start: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.76rem;
    font-weight: 600;
    color: var(--c-text-mute);
}
.cd-section__note ::v-deep .v-icon { color: var(--c-gold) !important; }

.cd-count {
    min-width: 24px;
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--c-purple-soft);
    color: #5b45d6;
    font-size: 0.76rem;
    font-weight: 800;
    text-align: center;
    font-feature-settings: "tnum" 1;
}

.cd-ltr-field ::v-deep input {
    direction: ltr;
    text-align: left;
}

.cd-hidden-submit {
    position: absolute;
    width: 0;
    height: 0;
    padding: 0;
    border: 0;
    overflow: hidden;
}

.cd-muted { color: var(--c-text-mute) !important; font-weight: 600 !important; }

/* sync info */
.cd-sync {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 18px;
    margin-top: 12px;
    padding: 9px 12px;
    border-radius: 10px;
    background: #f5f7fb;
    color: var(--c-text-soft);
    font-size: 0.84rem;
}
.cd-sync__item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}
.cd-sync__item ::v-deep .v-icon { color: var(--c-text-mute) !important; }
.cd-sync__item strong {
    color: var(--c-text);
    font-weight: 700;
}

/* add level */
.cd-add-level {
    display: grid;
    grid-template-columns: 1fr 140px auto;
    align-items: start;
    gap: 10px;
    margin-bottom: 14px;
}

/* levels list */
.cd-levels {
    border: 1px solid var(--c-border-soft);
    border-radius: 12px;
    overflow: hidden;
}

.cd-levels__head,
.cd-level {
    display: grid;
    grid-template-columns: 80px 1fr 1fr 96px;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
}

.cd-levels__head {
    height: 38px;
    background: #fafbfd;
    border-bottom: 1px solid var(--c-border-soft);
    color: var(--c-text-soft);
    font-size: 0.75rem;
    font-weight: 800;
}

.cd-levels__body {
    max-height: 300px;
    overflow-y: auto;
}

.cd-level {
    min-height: 46px;
    border-bottom: 1px solid var(--c-border-soft);
    font-size: 0.88rem;
    color: var(--c-text);
    transition: background 0.18s var(--c-ease);
}
.cd-level:last-child { border-bottom: none; }
.cd-level:hover { background: #fafbfd; }

.cd-level--new {
    animation: cd-flash 2.2s var(--c-ease);
}

@keyframes cd-flash {
    0%   { background: rgba(253, 183, 33, 0.28); }
    100% { background: transparent; }
}

.cd-level__code {
    justify-self: start;
    min-width: 44px;
    padding: 2px 9px;
    border-radius: 7px;
    background: #f3f5f9;
    color: var(--c-navy);
    font-weight: 800;
    text-align: center;
    font-feature-settings: "tnum" 1;
}

.cd-level__name {
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cd-level__ext {
    color: var(--c-text-soft);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cd-level__actions {
    display: flex;
    justify-content: flex-end;
}

.cd-level__delete {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 30px;
    padding: 0 8px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: var(--c-text-mute);
    font-family: inherit;
    font-size: 0.8rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.18s var(--c-ease);
}
.cd-level__delete ::v-deep .v-icon { color: inherit !important; }
.cd-level__delete:hover {
    color: var(--c-red);
    background: var(--c-red-soft);
}
.cd-level__delete--confirm {
    color: #fff !important;
    background: var(--c-red) !important;
    border-color: var(--c-red);
}
.cd-level__delete:disabled { opacity: 0.5; cursor: default; }

.cd-levels__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 26px 16px;
    color: var(--c-text-mute);
    font-size: 0.86rem;
    font-weight: 600;
    text-align: center;
}
.cd-levels__empty ::v-deep .v-icon { color: var(--c-text-mute) !important; }

/* list transitions */
.cd-level-enter-active,
.cd-level-leave-active {
    transition: opacity 0.25s var(--c-ease), transform 0.25s var(--c-ease);
}
.cd-level-enter,
.cd-level-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

/* ─── FOOTER ─── */
.cd-foot {
    border-top: 1px solid var(--c-border-soft);
    background: #ffffff;
}

.cd-foot__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 22px;
}

.cd-foot__dirty {
    margin-inline-end: auto;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    color: var(--c-amber);
    font-size: 0.82rem;
    font-weight: 700;
}
.cd-foot__dirty ::v-deep .v-icon { color: var(--c-amber) !important; }

/* ─── BUTTONS ─── */
.cd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 0.9rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s var(--c-ease);
    white-space: nowrap;
}
.cd-btn:disabled {
    opacity: 0.55;
    cursor: default;
    transform: none !important;
    box-shadow: none !important;
}

.cd-btn--primary {
    background: var(--c-navy);
    color: #fff;
}
.cd-btn--primary:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -4px rgba(13, 44, 109, 0.5);
}

.cd-btn--ghost {
    background: #ffffff;
    border-color: var(--c-border);
    color: var(--c-text-soft);
}
.cd-btn--ghost:not(:disabled):hover { background: #f5f7fb; }

.cd-btn--add {
    height: 40px;
    background: var(--c-purple);
    color: #fff;
}
.cd-btn--add:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px -4px rgba(128, 102, 240, 0.55);
}

/* ─── RESPONSIVE ─── */
@media (max-width: 600px) {
    .cd-head { padding: 14px 16px; }
    .cd-body { padding: 14px 12px 4px !important; }
    .cd-add-level { grid-template-columns: 1fr 100px; }
    .cd-btn--add { grid-column: 1 / -1; }
    .cd-levels__head { display: none; }
    .cd-level { grid-template-columns: 60px 1fr 80px; }
    .cd-level__ext { display: none; }
    .cd-foot__actions { flex-wrap: wrap; padding: 12px 14px; }
    .cd-foot__dirty { width: 100%; }
}
</style>

<style>
/* מחוץ ל-scoped כי Vuetify מרנדר את עטיפת הדיאלוג מחוץ לקומפוננטה */
.v-dialog.cd-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}
</style>